import { type PaginationState } from '@tanstack/react-table'
import { type Dispatch, type FC, type SetStateAction } from 'react'

import AfterDateFilter from 'components/brokers/needs_attention/AfterDateFilter'
import AssigneesFilter from 'components/brokers/needs_attention/AssigneesFilter'
import BeforeDateFilter from 'components/brokers/needs_attention/BeforeDateFilter'
import CarrierNameFilter from 'components/brokers/needs_attention/CarrierNameFilter'
import EnrollmentCategoryFilter from 'components/brokers/needs_attention/EnrollmentCategoryFilter'
import MemberFilter from 'components/brokers/needs_attention/MemberFilter'
import TaskHandlersFilter from 'components/brokers/tasks/TaskHandlersFilter'
import TasksExecutionMethodFilter from 'components/brokers/tasks/TasksExecutionMethodFilter'
import TasksStatusFilter from 'components/brokers/tasks/TasksStatusFilter'
import TaskTypesFilter from 'components/brokers/tasks/TaskTypesFilter'
import {
  type MultipleSelectedValuesFilter,
  type SingleSelectedValueFilter,
} from 'components/brokers/types'
import { type TasksFilters } from 'components/hooks/tasks/useGetTasks'
import EnterpriseFiltersView, { type FilterConfigurations } from 'components/reusable/filters/EnterpriseFiltersView'
import { type ComboboxFilterGroup } from 'components/reusable/table/ComboboxMultiSelectFilter'

export type TaskFilterGroupType = 'Date' | 'Task' | 'Associations' | 'Triaging'

type SingleSelectFilterComponents = Record<
  string,
  FC<SingleSelectedValueFilter>
>
type MultiSelectFilterComponents = Record<
  string,
  FC<MultipleSelectedValuesFilter>
>

export const tasksSingleSelectFilters: SingleSelectFilterComponents = {
  after_date: AfterDateFilter,
  before_date: BeforeDateFilter,
}

export const tasksMultiSelectFilters: MultiSelectFilterComponents = {
  statuses: TasksStatusFilter,
  execution_methods: TasksExecutionMethodFilter,
  handlers: TaskHandlersFilter,
  task_types: TaskTypesFilter,
  member_ids: MemberFilter,
  broker_ids: AssigneesFilter,
  carrier_names: CarrierNameFilter,
  enrollment_categories: EnrollmentCategoryFilter,
}

export const filterConfigurations: FilterConfigurations<
  TasksFilters,
  TaskFilterGroupType
> = {
  after_date: {
    key: 'after_date',
    label: 'After Date',
    group: 'Date',
    isMultiValue: false,
  },
  before_date: {
    key: 'before_date',
    label: 'Before Date',
    group: 'Date',
    isMultiValue: false,
  },
  statuses: {
    key: 'statuses',
    label: 'Status',
    group: 'Task',
    isMultiValue: true,
  },
  execution_methods: {
    key: 'execution_methods',
    label: 'Execution Method',
    group: 'Task',
    isMultiValue: true,
  },
  handlers: {
    key: 'handlers',
    label: 'Task Handler',
    group: 'Task',
    isMultiValue: true,
  },
  task_types: {
    key: 'task_types',
    label: 'Task Type',
    group: 'Task',
    isMultiValue: true,
  },
  member_ids: {
    key: 'member_ids',
    label: 'Member',
    group: 'Associations',
    isMultiValue: true,
  },
  broker_ids: {
    key: 'broker_ids',
    label: 'Assignee',
    group: 'Triaging',
    isMultiValue: true,
  },
  carrier_names: {
    key: 'carrier_names',
    label: 'Carrier',
    group: 'Associations',
    isMultiValue: true,
  },
  enrollment_categories: {
    key: 'enrollment_categories',
    label: 'Enrollment Category',
    group: 'Associations',
    isMultiValue: true,
  },
}

export const tasksDefaultPinnedOptionsByFilterValue = {
  statuses: [],
  execution_methods: [],
  handlers: [],
  task_types: [],
  member_ids: [],
  broker_ids: [{ label: 'Unassigned', value: 'unassigned' }],
}

export const getTasksFiltersConfigurationSortValue = (
  filterConfiguration: ComboboxFilterGroup<string>
): number => {
  switch (filterConfiguration.groupLabel) {
    case 'Triaging':
      return 0
    case 'Task':
      return 1
    case 'Date':
      return 2
    case 'Associations':
      return 3
    default:
      return 4
  }
}

type TasksFiltersViewProps = {
  filters: TasksFilters
  setFilters: Dispatch<SetStateAction<TasksFilters>>
  pagination: PaginationState
  setPagination: Dispatch<SetStateAction<PaginationState>>
}

const TasksFiltersView = ({
  filters,
  setFilters,
  pagination,
  setPagination,
}: TasksFiltersViewProps) => {
  return (
    <EnterpriseFiltersView
      filters={filters}
      setFilters={setFilters}
      pagination={pagination}
      setPagination={setPagination}
      filterConfigurations={filterConfigurations}
      singleSelectFilters={tasksSingleSelectFilters}
      multiSelectFilters={tasksMultiSelectFilters}
      defaultPinnedOptionsByFilterValue={tasksDefaultPinnedOptionsByFilterValue}
      getFiltersConfigurationSortValue={getTasksFiltersConfigurationSortValue}
    />
  )
}

export default TasksFiltersView
